import { Form as AntForm, Row } from 'antd';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { heightUpdatedEvent } from '@guuru/chat-events-web';
import {
  CHAT_INPUT_MODES,
  CHAT_VIEW_STATES,
  ChatAPI,
  useChatSettings,
  useDetectIframe,
  useCurrentChat,
  useLocalMessages,
} from '@guuru/chat-web';
import events from '@guuru/events-web';
import { ChatRowWrapper } from '@guuru/react-chat-messages';
import {
  ChatMessageBar,
  TermsAndConditions,
  Widget,
} from '@guuru/react-chatwidget';
import { usePageTitle } from '@guuru/react-hooks';
import LoadingView from '@guuru/react-loading';
import ChatAlert from '../../components/ChatAlert';
import {
  initChatFlow,
  recoverChatConnection,
  runNextChatStep,
  startNewQuestion,
  submitChatText,
} from '../../flow/chatFlowCommands';
import useChatWrapper from '../../hooks/useChatWrapper';
import useIsMobile from '../../hooks/useIsMobile';
import ChatConnectionRetryCta from '../chat/ChatConnectionRetryCta';
import ExpanderButton from './ExpanderButton';
import QuestionForm from './QuestionForm';
import { BottomBar, Welcome, WelcomeText } from './style';
import useSmartFormHeight from './useSmartFormHeight';

const Form = function () {
  usePageTitle('Form - Guuru');
  const { t } = useTranslation();
  const { chatAlignment, isFullScreen } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerId, chatControlToken, chatId: chatIdParam } = useParams();
  const [antForm] = AntForm.useForm();
  const didMount = useRef(false);
  const [question, setQuestion] = useState();
  const isMobile = useIsMobile();
  const {
    chatId,
    inputMode,
    isConnectionError,
    isRunningStep,
    loading,
    viewState,
  } = useCurrentChat();
  const localMessages = useLocalMessages();
  const smartFormQuestion = ChatAPI.storeRetrieveQuestion(partnerId);
  const inIframe = useDetectIframe();

  const {
    loading: isLoadingSettings,
    data: {
      chatSettings: {
        chatWidgetCustomBotAvatar,
        chatWidgetCustomBotAvatarPhotoUrl,
        chatWidgetShowPoweredBy,
      } = {},
    } = {},
  } = useChatSettings();

  const formHeight = useSmartFormHeight(
    inIframe && !loading && !isLoadingSettings,
  );

  useEffect(() => {
    if (!didMount.current) {
      initChatFlow({
        partnerId,
        chatId: chatIdParam,
        chatControlToken,
        navigate,
        location,
        options: { channel: 'form', payloadQuestion: question },
      });
      didMount.current = true;
    }
  }, [navigate, question, location, chatControlToken, chatIdParam, partnerId]);

  const handleConnectionError = () => {
    recoverChatConnection(partnerId);
  };

  const handleWidgetSubmitChatText = useCallback(
    (payload) => {
      submitChatText(partnerId, payload.chatId || chatId, payload);
    },
    [chatId, partnerId],
  );

  useChatWrapper();

  useEffect(() => {
    if (formHeight > 0) heightUpdatedEvent(formHeight);
  }, [formHeight]);

  const displayPoweredBy = useMemo(() => {
    const viewStatesWherePoweredByIsVisible = [
      CHAT_VIEW_STATES.question,
      CHAT_VIEW_STATES.email,
      CHAT_VIEW_STATES.name,
    ];

    return (
      chatWidgetShowPoweredBy &&
      viewStatesWherePoweredByIsVisible.includes(viewState)
    );
  }, [chatWidgetShowPoweredBy, viewState]);

  const handleAskNewQuestion = useCallback(() => {
    setQuestion(false);
    antForm.setFieldsValue({ question: undefined });

    events.userReloadChat();
    startNewQuestion(partnerId, navigate, location, { channel: 'form' });
  }, [antForm, location, navigate, partnerId]);

  const handleSubmit = useCallback(
    async (eventLabel, text, type) => {
      if (text.trim().length > 0) {
        events.submit(eventLabel);
        submitChatText(partnerId, chatId, {
          text,
          inputMode,
          textType: type || 'text',
          channel: 'smartForm',
        });
      }
    },
    [chatId, inputMode, partnerId],
  );

  const renderChatMessageBar = useCallback(
    ({ onAttachmentSubmit }) => {
      const isInputModeNone = [
        CHAT_INPUT_MODES.question,
        CHAT_INPUT_MODES.none,
      ].includes(inputMode);
      if (
        isInputModeNone ||
        (isRunningStep && viewState === CHAT_VIEW_STATES.automation) ||
        !inputMode ||
        !viewState
      ) {
        return null;
      }

      const typeAllowed = [
        CHAT_INPUT_MODES.name,
        CHAT_INPUT_MODES.email,
        CHAT_INPUT_MODES.chat,
      ].includes(inputMode);
      return (
        <BottomBar
          button={!typeAllowed}
          $transparent={inputMode === CHAT_INPUT_MODES.restart}
        >
          <ChatMessageBar
            isUser
            noFocus={isMobile}
            chatId={chatId}
            partnerId={partnerId}
            inputMode={inputMode}
            onSubmit={handleSubmit}
            onAttachmentSubmit={onAttachmentSubmit}
            isClosed={viewState === CHAT_VIEW_STATES.rate}
            isReviewingRate={viewState === CHAT_VIEW_STATES.rateBad}
            isFinished={
              inputMode === CHAT_INPUT_MODES.restart && !isRunningStep
            }
            onAskNewQuestion={handleAskNewQuestion}
          />
        </BottomBar>
      );
    },
    [
      chatId,
      handleAskNewQuestion,
      handleSubmit,
      inputMode,
      isMobile,
      isRunningStep,
      partnerId,
      viewState,
    ],
  );

  const onSubmitQuestion = useCallback(
    (q) => {
      setQuestion(q);
      handleSubmit('chatInit', q);
    },
    [setQuestion, handleSubmit],
  );

  const getMods = () => {
    let mods = 'mod--smartForm';
    if (displayPoweredBy) {
      mods = `${mods} mod--powered-by`;
    }
    if (isFullScreen) {
      mods = `${mods} mod--fullScreen`;
    }
    switch (viewState) {
      case CHAT_VIEW_STATES.chat:
        mods = `${mods} mod--chat`;
        break;
      case CHAT_VIEW_STATES.rate:
      case CHAT_VIEW_STATES.rateBad:
        mods = `${mods} mod--rate`;
        break;
      case CHAT_VIEW_STATES.connectingToExpert:
        mods = `${mods} mod--connecting`;
        break;
      case CHAT_VIEW_STATES.finished:
        mods = `${mods} mod--finished`;
        break;
      default:
        break;
    }
    return mods;
  };

  if (isLoadingSettings || loading) {
    return <LoadingView />;
  }

  const renderChatHeader = () => (
    <>
      <Welcome>
        <Row justify={chatAlignment}>
          <WelcomeText>
            {t('chat:::welcomeSmartForm')}
            <ExpanderButton
              hidden={
                (!question && !chatId && !isFullScreen) ||
                !isMobile ||
                !inIframe
              }
            />
          </WelcomeText>
        </Row>
      </Welcome>
      <ChatAlert />
      <ChatRowWrapper>
        <Row justify={chatAlignment}>
          <TermsAndConditions visible />
        </Row>
        <QuestionForm
          form={antForm}
          question={smartFormQuestion}
          onSubmit={onSubmitQuestion}
          showSubmit={viewState === CHAT_VIEW_STATES.question}
          isMobile={isMobile}
        />
      </ChatRowWrapper>
    </>
  );

  return (
    <Widget
      showTermsAndConditions={false}
      isUser
      chatId={chatId}
      localMessages={localMessages}
      viewState={viewState}
      inputMode={inputMode}
      showBotAvatar
      customBotAvatar={
        chatWidgetCustomBotAvatar ? chatWidgetCustomBotAvatarPhotoUrl : ''
      }
      baseMods={getMods()}
      renderChatMessageBar={renderChatMessageBar}
      renderChatHeader={renderChatHeader}
      onRunNextStep={runNextChatStep}
      onSubmitChatText={handleWidgetSubmitChatText}
    >
      <ChatConnectionRetryCta
        visible={isConnectionError}
        action={handleConnectionError}
      />
    </Widget>
  );
};

export default Form;
